import { useState, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { haptic } from '../lib/telegram.js';
import { Card, Empty, ErrorBox, Spinner } from '../components/ui.js';
import { DynamicField } from '../components/DynamicField.js';
import type { FieldDescriptor } from '../lib/types.js';

export function CreatePage(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [projectKey, setProjectKey] = useState(params.get('project') ?? '');
  const [issueTypeId, setIssueTypeId] = useState(params.get('type') ?? '');
  const [values, setValues] = useState<Record<string, unknown>>({});

  const projects = useQuery({
    queryKey: ['projects', 'create'],
    queryFn: () =>
      api<{ items: Array<{ key: string; name: string }> }>('/projects?action=create&maxResults=100'),
  });

  const issueTypes = useQuery({
    queryKey: ['issuetypes', projectKey],
    queryFn: () =>
      api<{ issueTypes: Array<{ id: string; name: string; subtask: boolean }> }>(
        `/projects/${projectKey}/issuetypes`,
      ),
    enabled: projectKey.length > 0,
  });

  const fields = useQuery({
    queryKey: ['createfields', projectKey, issueTypeId],
    queryFn: () =>
      api<{ fields: FieldDescriptor[] }>(
        `/projects/${projectKey}/issuetypes/${issueTypeId}/fields`,
      ),
    enabled: projectKey.length > 0 && issueTypeId.length > 0,
  });

  const create = useMutation({
    mutationFn: () =>
      api<{ issue: { key: string } }>('/issues', {
        method: 'POST',
        body: { projectKey, issueTypeId, fields: values },
      }),
    onSuccess: (result) => {
      haptic('success');
      navigate(`/issues/${result.issue.key}`);
    },
    onError: () => haptic('error'),
  });

  const editable = (fields.data?.fields ?? []).filter(
    (field) => !['project', 'issuetype'].includes(field.schema.system ?? ''),
  );
  const required = editable.filter((field) => field.required);
  const optional = editable.filter((field) => !field.required);

  return (
    <div className="page">
      <div className="header">
        <h1>{t('create.pick_project')}</h1>
      </div>

      <Card>
        <div className="field">
          <label htmlFor="project">{t('create.pick_project')}</label>
          <select
            id="project"
            value={projectKey}
            onChange={(event) => {
              setProjectKey(event.target.value);
              setIssueTypeId('');
              setValues({});
            }}
          >
            <option value="">—</option>
            {(projects.data?.items ?? []).map((project) => (
              <option key={project.key} value={project.key}>
                {project.key} · {project.name}
              </option>
            ))}
          </select>
        </div>

        {projectKey ? (
          <div className="field">
            <label htmlFor="type">{t('create.pick_type')}</label>
            <select
              id="type"
              value={issueTypeId}
              onChange={(event) => {
                setIssueTypeId(event.target.value);
                setValues({});
              }}
            >
              <option value="">—</option>
              {(issueTypes.data?.issueTypes ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </Card>

      {projects.error ? <ErrorBox error={projects.error} /> : null}
      {issueTypes.error ? <ErrorBox error={issueTypes.error} /> : null}
      {fields.isLoading && issueTypeId ? <Spinner /> : null}
      {fields.error ? <ErrorBox error={fields.error} /> : null}

      {editable.length > 0 ? (
        <>
          <Card title={t('common.confirm')}>
            {required.length === 0 ? <Empty text={t('common.none')} /> : null}
            {required.map((field) => (
              <DynamicField
                key={field.fieldId}
                field={field}
                value={values[field.fieldId]}
                onChange={(next) => setValues((prev) => ({ ...prev, [field.fieldId]: next }))}
                scope={{ projectKey }}
              />
            ))}
          </Card>

          {optional.length > 0 ? (
            <details>
              <summary className="section-title" style={{ cursor: 'pointer' }}>
                {t('common.more')}
              </summary>
              <div className="card">
                {optional.map((field) => (
                  <DynamicField
                    key={field.fieldId}
                    field={field}
                    value={values[field.fieldId]}
                    onChange={(next) => setValues((prev) => ({ ...prev, [field.fieldId]: next }))}
                    scope={{ projectKey }}
                  />
                ))}
              </div>
            </details>
          ) : null}

          <button
            type="button"
            className="btn btn-block"
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            {t('common.save')}
          </button>
          {create.error ? <ErrorBox error={create.error} /> : null}
        </>
      ) : null}
    </div>
  );
}
